import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, RefreshCw, LogOut, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmailNotVerified = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const resendVerificationEmail = async () => {
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSent(true);
      toast.success('Email de vérification envoyé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center px-4" data-testid="email-not-verified-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <Mail className="w-10 h-10 text-amber-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">Vérifiez votre email</CardTitle>
          <CardDescription className="text-base mt-2">
            Pour accéder à drbooking, veuillez vérifier votre adresse email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 text-center">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
              Un email a été envoyé à :
            </p>
            <p className="font-medium text-slate-900 dark:text-white">
              {user?.email}
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
              Cliquez sur le lien dans l'email pour activer votre compte. 
              Vérifiez aussi votre dossier spam.
            </p>
            
            {sent ? (
              <div className="flex items-center justify-center gap-2 text-green-600 py-3">
                <CheckCircle className="w-5 h-5" />
                <span>Email envoyé !</span>
              </div>
            ) : (
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={resendVerificationEmail}
                disabled={sending}
                data-testid="resend-email-btn"
              >
                {sending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Renvoyer l'email de vérification
              </Button>
            )}
          </div>

          <div className="pt-4 border-t">
            <Button 
              variant="ghost" 
              className="w-full text-slate-500" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Se déconnecter
            </Button>
          </div>

          <p className="text-xs text-center text-slate-400">
            Besoin d'aide ? <a href="/contact" className="text-primary hover:underline">Contactez-nous</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailNotVerified;
